import React, { useState, useEffect, useRef } from 'react';
import { Send, Loader2, AlertCircle, ShieldCheck, Headset, User, Check, CheckCheck } from 'lucide-react';
import Cookies from 'js-cookie';
import { useAuth } from "../context/AuthProvider";
import { BASE_URL } from '../apiConfig';
import { Link } from 'react-router-dom';

export const ChatWindow = ({ reclamation, onClose, position = 0, onMessageSent }) => {
  const [messages, setMessages] = useState(reclamation.messages || []);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const { token } = useAuth();
  const user = Cookies.get('user') ? JSON.parse(Cookies.get('user')) : null;
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    setMessages(reclamation.messages || []);
  }, [reclamation]);

  useEffect(() => {
    scrollToBottom();
    markMessagesAsSeen();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || sending) return;
    setSending(true);
    setError(null);

    try {
      let url = `${BASE_URL}/api/reclamations`;
      let method = 'POST';
      let payload;

      if (reclamation.isNew) {
        url = `${BASE_URL}/api/reclamations/create-with-message`;
        payload = {
          order_id: reclamation.order_id,
          message: newMessage.trim(),
          status: 'notseen'
        };
      } else {
        url = `${BASE_URL}/api/reclamations`;
        payload = {
          order_reclamations_id: reclamation.id,
          message: newMessage.trim(),
          status: 'notseen'
        };
      }

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Failed to send message');
      const sentMessage = await response.json();

      if (reclamation.isNew) {
        setShouldRefresh(true);
        onMessageSent?.();
      } else {
        setMessages(prev => [...prev, sentMessage]);
      }

      setNewMessage('');
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const markMessagesAsSeen = async () => {
    const unseenMessages = messages.filter(msg => msg.status === 'notseen');
    if (unseenMessages.length === 0) return;

    try {
      await fetch(`${BASE_URL}/api/reclamations/mark-seen`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reclamation_id: reclamation.id }),
      });
    } catch (err) {
      console.error('Error marking messages as seen:', err);
    }
  };


  useEffect(() => {
    if (shouldRefresh && reclamation.isNew) {
      onClose();
      
      setTimeout(() => {
        onMessageSent?.();
      }, 100);
      
      setShouldRefresh(false);
    }
  }, [shouldRefresh, reclamation.isNew, onClose, onMessageSent]);

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  const groupMessagesByDate = () => {
    const groups = {};
    messages.forEach(message => {
      const dateKey = formatDate(message.created_at);
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(message);
    });
    return groups;
  };

  const isCurrentUserMessage = (messageUser) => {
    return user && messageUser && messageUser.email === user.email;
  };

  // Calculate position based on chat index
  const rightOffset = 16 + (position * 336);

  return (
    <div 
      className="fixed bottom-0 w-80 h-96 bg-white rounded-t-lg shadow-lg flex flex-col z-50 border border-gray-200"
      style={{ right: `${rightOffset}px` }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-[var(--color-primary-500)] text-white rounded-t-lg">
        <div className="flex items-center gap-2 text-sm font-semibold truncate">
          <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
          Commande #{reclamation.order.code}
        </div>
        <Link
          onClick={onClose}
          to='/reclamations'
          className="text-white cursor-pointer hover:text-gray-200 text-xl leading-none font-bold"
        >
          &times;
        </Link>
      </div>

      {/* Messages */}
      <div className="flex-1 p-4 overflow-y-auto bg-gray-50 custom-scrollbar">
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-gray-500 text-[10px] mt-12">
            Aucun message pour le moment.
          </div>
        ) : (
          Object.entries(groupMessagesByDate()).map(([date, dateMessages]) => (
            <div key={date}>
              <div className="text-center text-xs text-gray-500 my-4">
                {date}
              </div>
              {dateMessages.map((message) => {
                const isOwnMessage = isCurrentUserMessage(message.user);
                
                return (
                  <div
                    key={message.id}
                    className={`mb-3 flex ${isOwnMessage ? "justify-end" : "justify-start"}`}
                  >
                    <div className={`max-w-[80%] ${isOwnMessage ? "ml-auto" : ""}`}>
                      {!isOwnMessage && (
                        <div className="flex gap-1 items-center text-[10px] text-gray-500 mb-1">
                          {message.user ? message.user.info.name : 'System'} 
                          {
                            message.user.role === 1 || message.user.role === 2 ?
                            <div className='flex items-center gap-0.5 text-[10px] px-1 text-blue-500 bg-blue-100 rounded-full'>
                              <ShieldCheck size={12}/>
                              Admin
                            </div>
                            :
                            message.user.role === 3 ?
                            <div className='flex items-center gap-0.5 text-[10px] px-1 text-green-600 bg-green-100 rounded-full'>
                              <Headset size={12} />
                              Confirmateur
                            </div>
                            :
                            <div className='flex items-center gap-0.5 text-[10px] px-1 text-amber-500 bg-amber-100 rounded-full'>
                              <User size={12} />
                              Vendeur
                            </div>
                          }
                        </div>
                      )}
                      
                      <div className={`flex ${isOwnMessage ? "flex-row-reverse" : "flex-row"} items-end`}>
                        
                        {isOwnMessage && (
                          <span className={`mb-1 ${isOwnMessage ? "ml-1" : "mr-1"}`}>
                            {message.status === "seen" ? (
                              <CheckCheck size={12} className="text-blue-500" />
                            ) : (
                              <Check size={12} className="text-gray-400" />
                            )}
                          </span>
                        )}
                        <div
                          className={`px-3 py-1.5 text-xs rounded-2xl ${
                            isOwnMessage
                              ? "bg-[var(--color-primary-500)] text-white"
                              : "bg-gray-200 text-gray-900"
                          }`}
                        >
                          {message.message}
                        </div>
                      </div>
                      
                      <div className={`text-[9px] text-gray-400 mt-1 ${isOwnMessage ? "text-right" : "text-left"}`}>
                        {formatTime(message.created_at)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
        
        {/* Invisible element at the bottom for auto-scrolling */}
        <div ref={messagesEndRef} />
        
        {error && (
          <div className="flex items-center gap-2 text-red-500 text-xs bg-red-50 p-2 rounded mb-3">
            <AlertCircle size={14} />
            {error}
          </div>
        )}
      </div>

      {/* Input */}
      <div className="border-t border-gray-300 px-4 py-3 flex items-center gap-2">
        <input
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Tapez un message..."
          className="flex-1 text-xs border border-gray-300 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-500)]"
          onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
          disabled={sending}
        />
        <button
          onClick={handleSendMessage}
          disabled={sending || !newMessage.trim()}
          className="bg-[var(--color-primary-500)] text-white p-2 rounded-full hover:bg-[#089c87] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {sending ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <Send size={16} />
          )}
        </button>
      </div>
    </div>
  );
};